// StoreListPanel — Phase 5a stores endpoint.

import { useMemo, useState } from 'react';
import { Card } from '../../primitives';
import {
  EmptyState,
  ErrorState,
  HeatCell,
  LoadingState,
  SectionHead,
  ShareBar,
  SortCaret,
} from '../bits';
import { useStores, type AnalyticsScope } from '../queries';
import {
  fmtNum,
  fmtWonShort,
  platformColor,
  platformShort,
} from '../../../lib/format';
import type { AnalyticsStoreRow } from '../../../types/api';

interface Props {
  scope: AnalyticsScope;
  onSelectStore?: (storeId: string, platformId?: string) => void;
  selectedStoreId?: string | null;
}

type SortKey = 'name' | 'revenue' | 'orders' | 'costRate' | 'delta';

export function StoreListPanel({ scope, onSelectStore, selectedStoreId }: Props) {
  const q = useStores(scope);
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
    key: 'revenue',
    dir: 'desc',
  });

  const sorted = useMemo<AnalyticsStoreRow[]>(() => {
    if (!q.data) return [];
    return [...q.data].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av ?? '').localeCompare(String(bv ?? ''));
      return cmp * (sort.dir === 'asc' ? 1 : -1);
    });
  }, [q.data, sort]);

  const max = useMemo(() => {
    if (!sorted.length) return 1;
    return Math.max(...sorted.map((s) => s.revenue));
  }, [sorted]);

  const Th = ({
    keyId,
    label,
    align,
  }: {
    keyId: SortKey;
    label: string;
    align?: 'right';
  }) => (
    <div
      onClick={() =>
        setSort((s) => ({
          key: keyId,
          dir: s.key === keyId && s.dir === 'desc' ? 'asc' : 'desc',
        }))
      }
      style={{
        cursor: 'pointer',
        textAlign: align ?? 'left',
        userSelect: 'none',
        padding: '6px 8px',
        fontSize: 11,
        fontWeight: 600,
        color: 'var(--fg-3)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
      }}
    >
      {label}
      <SortCaret dir={sort.key === keyId ? sort.dir : null} />
    </div>
  );

  return (
    <Card style={{ padding: 20 }}>
      <SectionHead title="상점별 매출" sub="기간 내 합계" />
      {q.isLoading && <LoadingState />}
      {q.isError && <ErrorState message={(q.error as Error).message} />}
      {q.data && q.data.length === 0 && <EmptyState />}
      {sorted.length > 0 && (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1.6fr 1.6fr 0.9fr 0.9fr 0.7fr',
              alignItems: 'center',
            }}
          >
            <Th keyId="name" label="상점" />
            <Th keyId="revenue" label="매출" />
            <Th keyId="orders" label="주문" align="right" />
            <Th keyId="costRate" label="원가율" align="right" />
            <Th keyId="delta" label="증감" align="right" />
          </div>
          <div
            style={{
              height: 1,
              background: 'var(--border-weak)',
              margin: '0 0 4px',
            }}
          />
          {sorted.map((s) => {
            const active = selectedStoreId === s.storeId;
            const clickable = !!onSelectStore;
            return (
            <div
              key={s.storeId}
              onClick={
                clickable
                  ? () => onSelectStore!(s.storeId, s.platformId)
                  : undefined
              }
              style={{
                display: 'grid',
                gridTemplateColumns: '1.6fr 1.6fr 0.9fr 0.9fr 0.7fr',
                alignItems: 'center',
                padding: '8px',
                borderRadius: 6,
                cursor: clickable ? 'pointer' : 'default',
                background: active ? 'var(--primary-bg)' : 'transparent',
                border: active
                  ? '1px solid var(--primary-border)'
                  : '1px solid transparent',
                transition: 'background 120ms',
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: 'var(--fg-1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: 999,
                    background: platformColor(s.platformId),
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {s.name}
                </span>
                <span
                  style={{
                    fontSize: 10,
                    color: 'var(--fg-3)',
                    padding: '1px 5px',
                    background: 'var(--bg-muted)',
                    borderRadius: 3,
                    flexShrink: 0,
                  }}
                >
                  {platformShort(s.platformId)}
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <ShareBar
                    value={s.revenue}
                    max={max}
                    color={platformColor(s.platformId)}
                  />
                </div>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--fg-1)',
                    fontVariantNumeric: 'tabular-nums',
                    minWidth: 54,
                    textAlign: 'right',
                  }}
                >
                  {fmtWonShort(s.revenue)}
                </span>
              </div>
              <div
                style={{
                  textAlign: 'right',
                  fontSize: 13,
                  color: 'var(--fg-2)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {fmtNum(s.orders)}
              </div>
              <div style={{ textAlign: 'right' }}>
                {s.costRate != null ? <HeatCell value={s.costRate} /> : '-'}
              </div>
              <div
                style={{
                  textAlign: 'right',
                  fontSize: 12,
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                  color:
                    s.delta != null && s.delta < 0
                      ? 'var(--danger)'
                      : 'var(--success)',
                }}
              >
                {s.delta != null
                  ? `${s.delta >= 0 ? '+' : ''}${s.delta.toFixed(1)}%`
                  : '-'}
              </div>
            </div>
            );
          })}
        </>
      )}
    </Card>
  );
}
